// FileUpload Component for FRACAS Attachments
// Handles photo, document, and video uploads with preview
// ============================================================

import React, { useState, useRef, useCallback } from 'react';
import { DropZone } from './FileUpload/DropZone';
import { FileItem } from './FileUpload/FileItem';
import { useFileUploadLogic, type UploadedFile } from '../../hooks/useFileUploadLogic';

export type { UploadedFile };

interface FileUploadProps {
    files: UploadedFile[];
    onFilesChange: (files: UploadedFile[]) => void;
    maxFiles?: number;
    maxSizeMB?: number;
    acceptedTypes?: string[];
    label?: string;
    description?: string;
    disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
    files,
    onFilesChange,
    maxFiles = 10,
    maxSizeMB = 25,
    acceptedTypes = ['image/*', 'application/pdf', '.doc', '.docx', '.xls', '.xlsx', 'video/*'],
    label = 'Attachments',
    description = 'Upload photos, documents, or videos',
    disabled = false,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        uploading,
        error,
        processFiles
    } = useFileUploadLogic(files, onFilesChange, maxFiles, maxSizeMB, disabled);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
    }, [disabled]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files) processFiles(e.dataTransfer.files);
    }, [processFiles]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) processFiles(e.target.files);
    }, [processFiles]);

    const handleRemoveFile = useCallback((fileId: string) => {
        onFilesChange(files.filter(f => f.id !== fileId));
    }, [files, onFilesChange]);

    const handleDescriptionChange = useCallback((fileId: string, description: string) => {
        onFilesChange(files.map(f => f.id === fileId ? { ...f, description } : f));
    }, [files, onFilesChange]);

    return (
        <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">{label}</label>

            <DropZone
                isDragging={isDragging}
                uploading={uploading}
                disabled={disabled}
                description={description}
                maxSizeMB={maxSizeMB}
                acceptedTypes={acceptedTypes}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !disabled && fileInputRef.current?.click()}
                fileInputRef={fileInputRef}
                onFileSelect={handleFileSelect}
            />

            {error && <p className="text-sm text-red-600">{error}</p>}

            {files.length > 0 && (
                <div className="space-y-3">
                    <p className="text-sm text-gray-500">{files.length} file(s) attached</p>
                    <div className="grid gap-3">
                        {files.map((file) => (
                            <FileItem
                                key={file.id}
                                file={file}
                                disabled={disabled}
                                onRemove={handleRemoveFile}
                                onDescriptionChange={handleDescriptionChange}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FileUpload;
